/**
 * IP cluster registry — which schools share a year-by-year IP topic sequence.
 *
 * What: A cluster is a set of schools whose IP syllabus places the same topics
 * in the same years and the same in-year order. It is a **sequencing** concept
 * only: it becomes the optional third segment of a stream key
 * (`v1:secondary_1|IP|<cluster>`) and selects which stored topic order applies.
 * It is never a content-membership dimension — topics, KG nodes, questions,
 * mastery, calibration priors and diagnostic pools all stay cluster-blind.
 *
 * Why code, not a collection: cluster definitions change on the order of once
 * per school we onboard, always alongside the order migration that gives the
 * cluster something to sequence. Shipping the pair in one PR keeps a registered
 * cluster from ever pointing at an order that does not exist yet, and keeps the
 * admin panel's job down to "which school is this student at".
 *
 * When: Add an entry here in the same PR as that cluster's order migration.
 * Removing an entry is safe at any time — every resolver degrades to the base
 * (cluster-less) key, so a removed cluster's stored order simply goes dormant.
 *
 * MUST NOT import `shared/schools/schoolData.ts`. The admin enrollment dialog
 * imports this module, and that catalog is a ~290KB generated file that would
 * be inlined into a client chunk. Registry invariants that need the catalog
 * (slug exists, school serves secondary) are enforced by the unit test, which
 * may import it freely.
 */
import type { ExamLevel } from './examLevels.js';
import { STREAM_CLUSTER_SEGMENT_REGEX } from './streamKey.js';

/** One cluster: an id used in stream keys, a human label, its member schools. */
export interface IpClusterDef {
  /** Stream-key segment. Lowercase; see `IP_CLUSTER_ID_REGEX`. */
  id: string;
  /** Shown to staff in the admin enrollment dialog, e.g. "Hwa Chong Institution". */
  label: string;
  /** Catalog slugs (`SchoolRecord.slug`) of the schools on this sequence. */
  schoolSlugs: readonly string[];
}

/**
 * Cluster ids are 2–32 chars, lowercase-alpha first. This *is* the third
 * segment of `STREAM_KEY_REGEX`, not a copy of it, so a registered id can
 * never be one the key grammar rejects: Mongoose Map keys forbid `.` and `$`,
 * and the key grammar forbids `|`, so the charset excludes all three by
 * construction.
 */
export const IP_CLUSTER_ID_REGEX = STREAM_CLUSTER_SEGMENT_REGEX;

/**
 * The registry.
 *
 * `hci` is first because its sequence was already here: what the platform
 * serves today as "Sec 2 IP" is Hwa Chong Institution's chapter order, adopted
 * back when IP meant one school. Migration 113 copies that order onto
 * `v1:secondary_2|IP|hci`, which pins HCI students to it before the base IP
 * order is re-based onto the generic four-year reference. HCI's Sec 1, 3 and 4
 * carry no cluster key: today's Sec 1 IP order is a mirror of Sec 1 G3 rather
 * than anything HCI supplied, and Sec 3/4 IP have no order at all, so those
 * years fall back to the base sequence until the school's own sequence arrives.
 */
export const IP_CLUSTERS: readonly IpClusterDef[] = [
  {
    id: 'hci',
    label: 'Hwa Chong Institution',
    schoolSlugs: ['hwa-chong-institution'],
  },
];

/**
 * Lookup indexes over the ACTIVE registry — `IP_CLUSTERS` everywhere except a
 * test that has installed an override.
 */
let clusterById: ReadonlyMap<string, IpClusterDef> = new Map();
let clusterIdBySchoolSlug: ReadonlyMap<string, string> = new Map();

function indexClusters(clusters: readonly IpClusterDef[]): void {
  clusterById = new Map(clusters.map(cluster => [cluster.id, cluster] as const));
  clusterIdBySchoolSlug = new Map(
    clusters.flatMap(cluster =>
      cluster.schoolSlugs.map(slug => [slug, cluster.id] as const)
    )
  );
}

indexClusters(IP_CLUSTERS);

/**
 * Test-only — run the resolvers against a fabricated registry, `null` to
 * restore the shipped one.
 *
 * Cluster-aware paths (order fallback, topic-graph ordering, the structure
 * lint's per-cluster fan-out, staff-sim acceptance) are tested against a
 * fabricated registry rather than the shipped one, so a school leaving or
 * joining — or the registry being empty again — can never turn a coverage
 * assertion into a vacuous one. Mirrors the `_…ForTests` helpers on
 * `syllabusOrderService`; the frontend does the same job with `vi.mock`.
 */
export function _setIpClustersForTests(
  clusters: readonly IpClusterDef[] | null
): void {
  indexClusters(clusters ?? IP_CLUSTERS);
}

/**
 * True only for an id that is both well-formed AND currently registered.
 *
 * Used to sanitize cluster ids arriving from stored documents and staff-sim
 * query params: a cluster deleted from the registry stops being accepted the
 * moment its entry goes, and callers fall back to the base key.
 */
export function isIpClusterId(id: unknown): id is string {
  return (
    typeof id === 'string' &&
    IP_CLUSTER_ID_REGEX.test(id) &&
    clusterById.has(id)
  );
}

/** The full definition for a registered id (for labels), else null. */
export function ipClusterById(id: string | null | undefined): IpClusterDef | null {
  if (typeof id !== 'string') return null;
  return clusterById.get(id) ?? null;
}

/**
 * Which cluster a school belongs to, or null when the school is unmapped.
 * Never throws — an unset, blank, or unknown slug is the common case, not an
 * error.
 */
export function clusterForSchoolSlug(
  slug: string | null | undefined
): string | null {
  if (typeof slug !== 'string') return null;
  const normalized = slug.trim().toLowerCase();
  if (normalized.length === 0) return null;
  return clusterIdBySchoolSlug.get(normalized) ?? null;
}

/**
 * The one resolver every call site uses: a student's cluster, given their exam
 * level and school.
 *
 * Null unless the student is on IP. School is an IP concept here — a G3 student
 * at a school that runs an IP cluster still follows the G3 sequence, because
 * the cluster describes that school's *IP* syllabus and nothing else.
 */
export function resolveIpCluster(
  examLevel: ExamLevel | string | null | undefined,
  schoolSlug: string | null | undefined
): string | null {
  if (examLevel !== 'IP') return null;
  return clusterForSchoolSlug(schoolSlug);
}
